import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Award } from "lucide-react";

export default function InvestorPerformanceTable({ investments, investors }) {
  // Calculate performance metrics for each investor
  const investorPerformance = investors.map(investor => {
    const investorInvests = investments.filter(inv => inv.investor_id === investor.id);
    
    const totalInvested = investorInvests.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
    const activeCount = investorInvests.filter(inv => inv.status === "active").length;
    const exitedCount = investorInvests.filter(inv => inv.status === "exited").length;
    
    const activeInvests = investorInvests.filter(inv => inv.status === "active");
    const exitedInvests = investorInvests.filter(inv => inv.status === "exited");
    
    const totalCurrentValue = activeInvests.reduce((sum, inv) => sum + (inv.current_valuation || inv.investment_amount || 0), 0);
    const totalExitValue = exitedInvests.reduce((sum, inv) => sum + (inv.exit_amount || 0), 0);
    const totalValue = totalCurrentValue + totalExitValue;
    
    const valueIncrease = totalValue - totalInvested;
    
    return {
      investor,
      totalInvested,
      activeCount,
      exitedCount,
      totalCount: investorInvests.length,
      valueIncrease,
      avgInvestment: totalInvested / (investorInvests.length || 1),
    };
  }).filter(item => item.totalCount > 0);

  const sortedPerformance = investorPerformance.sort((a, b) => b.totalInvested - a.totalInvested);

  return (
    <Card className="p-6 bg-white border-0 shadow-sm mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
          <Award className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Investor Performance</h3>
          <p className="text-sm text-slate-500">Detaljert oversikt over hver investor</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Investor</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Total Investert</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Antall Invest.</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Status</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Verdiøkning</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Snitt Investering</th>
            </tr>
          </thead>
          <tbody>
            {sortedPerformance.map((item, i) => (
              <tr key={item.investor.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4">
                  <div>
                    <p className="font-medium text-slate-900">{item.investor.name}</p>
                    <p className="text-xs text-slate-500">{item.investor.type}</p>
                  </div>
                </td>
                <td className="text-right py-3 px-4">
                  <span className="font-semibold text-slate-900">
                    {(item.totalInvested / 1000000).toFixed(2)}M kr
                  </span>
                </td>
                <td className="text-center py-3 px-4">
                  <Badge variant="secondary">{item.totalCount}</Badge>
                </td>
                <td className="text-center py-3 px-4">
                  <div className="flex justify-center gap-1">
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                      {item.activeCount} aktive
                    </Badge>
                    {item.exitedCount > 0 && (
                      <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">
                        {item.exitedCount} exit
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="text-right py-3 px-4">
                  <div className="flex items-center justify-end gap-1">
                    {item.valueIncrease >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-600" />
                    )}
                    <span className={`font-semibold ${item.valueIncrease >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {item.valueIncrease >= 0 ? "+" : ""}{(item.valueIncrease / 1000000).toFixed(2)}M kr
                    </span>
                  </div>
                </td>
                <td className="text-right py-3 px-4 text-slate-700">
                  {(item.avgInvestment / 1000000).toFixed(2)}M kr
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}